<template>
  <div class="relative">
    <component
      :is="$slots.trigger ? 'button' : IconButton"
      ref="triggerEl"
      v-on-click-outside="closeMenu"
      :disabled="disabled"
      data-cy="action-menu-button"
      :icon="iconType === 'vertical' ? IconVerticalDots : IconHorizontalDots"
      @click.stop.prevent="toggleMenu"
    >
      <slot v-if="$slots.trigger" name="trigger" :show-menu="showMenu" />
      <icon-base
        v-else
        class="my-1 cursor-pointer place-self-center"
        height="24"
        width="24"
        :icon="iconType === 'vertical' ? IconVerticalDots : IconHorizontalDots"
      />
    </component>
    <ul
      v-if="teleportMenu && showMenu"
      ref="teleportedDropdown"
      class="dropdown"
      data-cy="action-menu-list"
    >
      <slot />
    </ul>
    <ul v-else v-show="showMenu" class="dropdown" data-cy="action-menu-list">
      <slot />
    </ul>
  </div>
</template>

<script lang="ts">
// The reason behind duplicating the ActionMenu into this component is because
// the ActionMenu relied on the legacy way of teleporting an element and tracking
// the position of it's parent. ActionMenu is used widely in the app and it relies
// on the "top" and "left" props, also some other params which have effect on the
// teleported element. This component doesn't (in case of teleport). To avoid
// having breaking changes, it was easier to have a component that sticks to the
// new teleport system and slowly migrate away from the legacy one.
import { useElementVisibility } from "@vueuse/core";
import { type PropType, ref, useTemplateRef, watch } from "vue";

import { useTeleportV2 } from "@/hooks/elements";

import IconHorizontalDots from "@/components/icons/IconHorizontalDots.vue";
import IconVerticalDots from "@/components/icons/IconVerticalDots.vue";
import IconButton from "@/components/ui/buttons/IconButton.vue";

import type { Offset } from "@/models/common";
</script>

<script setup lang="ts">
const props = defineProps({
  iconType: {
    type: String,
    default: "vertical",
  },
  teleportMenu: {
    type: Boolean,
    default: false,
  },
  offset: {
    type: Object as PropType<Offset>,
    default: () => ({}),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  checkForOverflow: {
    type: Boolean,
    default: false,
  },
  closeMenuOnTriggerHide: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  "menu:toggle": [boolean];
}>();

const teleportedDropdown = useTemplateRef("teleportedDropdown");
const showMenu = ref(false);
const triggerEl = useTemplateRef("triggerEl");
const triggerVisible = useElementVisibility(triggerEl);

const toggleMenu = () => {
  if (!showMenu.value) {
    document.body.click(); // close any open menus
  }
  showMenu.value = !showMenu.value;
  emit("menu:toggle", showMenu.value);
};

if (props.teleportMenu) {
  useTeleportV2({
    target: teleportedDropdown,
    to: "#menus",
    offset: props.offset,
    checkForOverflow: props.checkForOverflow,
  });
}

const closeMenu = () => {
  showMenu.value = false;
  emit("menu:toggle", showMenu.value);
};

watch(triggerVisible, (isVisible) => {
  if (showMenu.value && !isVisible && props.closeMenuOnTriggerHide) {
    closeMenu();
  }
});

defineExpose({
  showMenu,
  closeMenu,
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.dropdown {
  @apply absolute z-101 w-max overscroll-none rounded-md bg-white p-2 text-sm text-gray-500 shadow-md focus:outline-hidden sm:text-sm;
}
</style>
